import React, { useState } from 'react'

import { Stack } from '@mui/material'

import { Button, Card, FieldLabel, Input } from '@repo/ui'
import { FileUploadField } from 'components'
import { useSnackbar } from 'contexts'

import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_COUNT,
  ATTACHMENT_MAX_SIZE_MB,
  uploadAttachments,
} from '../attachments'
import { MESSAGE_FIELD_SX } from '../messageField'

/**
 * The reply box on a support thread — text AND attachments, for both sides of the desk.
 *
 * ## Why it can attach files at all
 *
 * The intake accepted attachments from the start; a reply did not. That asymmetry is
 * worse than it sounds, because the single most common thing a support agent types is
 * "can you send a screenshot?" — and the answer to that question had nowhere to go. The
 * only workaround was to raise a SECOND request just to carry the file, which splits one
 * conversation across two threads and loses the context that made the request legible.
 *
 * The gap was purely in the UI. `POST /support/:id/messages` and
 * `POST /internal/support/:id/messages` have both accepted `attachmentUrls` since the
 * thread was built (see `support.controller.ts` / `support-internal.controller.ts`), and
 * `AttachmentList` already renders `message.attachmentUrls` on every reply — so a reply
 * with attachments has always DISPLAYED correctly. Nothing could produce one.
 *
 * ## Why a component rather than JSX inside RequestThread
 *
 * `RequestThread` renders this on the brand thread; the internal queue's staff reply is
 * the same control with a different label and a different mutation. That is two call
 * sites for one thing, which is the §36 line — and it is exactly the kind of thing that
 * drifts invisibly, because the fiddly part is not the markup. It is the upload
 * orchestration: on-submit rather than on-select, `allSettled` rather than `all`, and a
 * failed upload that must NOT eat the text. A copy would keep the textarea and quietly
 * lose one of those three.
 *
 * @see attachments.ts for the upload contract those three rules live in.
 */
export interface ReplyPayload {
  body: string
  /** Every attachment that uploaded successfully. Empty array when none were attached. */
  attachmentUrls: string[]
}

interface Props {
  /** Field label. "Reply" on the brand thread, "Reply as Vera" on the internal queue. */
  label: string
  placeholder: string
  /**
   * Post the reply. Resolves on success — the composer clears itself then, and ONLY
   * then, so a failed send never silently discards what the user wrote.
   *
   * Rejections are swallowed here on purpose: the caller owns its own error message
   * (it knows which surface and which mutation failed), and this component's job on a
   * rejection is simply to keep the draft on screen.
   */
  onSend: (payload: ReplyPayload) => Promise<unknown>
  /** True while the caller's mutation is in flight. Drives the button's disabled state. */
  isPending?: boolean
}

export const ReplyComposer = ({ label, placeholder, onSend, isPending = false }: Props) => {
  const { open: notification } = useSnackbar()

  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)

  /**
   * Bumped after a successful send to REMOUNT `FileUploadField`.
   *
   * That component holds its chosen files in its own state and exposes no imperative
   * clear — `items` seeds from URLs, which is the wrong direction here. Without this the
   * thumbnails of a just-sent screenshot stay in the dropzone under the now-empty
   * textarea, which reads as "it did not send" and invites the user to attach it twice.
   *
   * Remounting is not a workaround for a missing API so much as the honest expression of
   * "this composer is finished, here is a fresh one".
   */
  const [pickerKey, setPickerKey] = useState(0)

  const busy = isUploading || isPending
  const canSend = body.trim().length > 0 && !busy

  const send = async () => {
    if (!canSend) return

    let attachmentUrls: string[] = []

    if (attachments.length > 0) {
      setIsUploading(true)
      try {
        const result = await uploadAttachments(attachments)
        attachmentUrls = result.urls

        // A FAILED UPLOAD MUST NOT EAT THE REPLY. Say plainly what did not happen and
        // send the rest — the same rule the intake follows, worded for this surface.
        if (result.failed.length > 0) {
          notification({
            type: 'error',
            title:
              result.failed.length === attachments.length
                ? `Couldn't attach ${result.failed.join(', ')}. Your reply was sent without ${
                    result.failed.length === 1 ? 'it' : 'them'
                  }.`
                : `Couldn't attach ${result.failed.join(', ')}. Your reply was sent with the rest.`,
          })
        }
      } finally {
        setIsUploading(false)
      }
    }

    try {
      await onSend({ body: body.trim(), attachmentUrls })
      setBody('')
      setAttachments([])
      setPickerKey(key => key + 1)
    } catch {
      // Caller notified. Keep the draft and the files exactly where they are.
    }
  }

  return (
    <Card sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Input
          label={label}
          placeholder={placeholder}
          value={body}
          onValueChange={setBody}
          multiline
          minRows={4}
          // 14px, not the shared field default of 16 — what is typed here becomes a
          // message rendered at 14 directly above the box. See `MESSAGE_FIELD_SX`.
          sx={MESSAGE_FIELD_SX}
        />

        {/* The label comes from `FieldLabel` rather than FileUploadField's own `label`
            prop, which uses a bare body2 + 16px gap — the same call the intake makes,
            and for the same reason (§31: one label treatment per form). */}
        <Stack spacing={2}>
          <FieldLabel label="Attachments" adornment="(optional)" />
          <FileUploadField
            key={pickerKey}
            acceptFiles={ATTACHMENT_ACCEPT}
            description="Images, PDF or video"
            maxFiles={ATTACHMENT_MAX_COUNT}
            maxSizeMb={ATTACHMENT_MAX_SIZE_MB}
            showMaxFileSize
            // Same 140px as the intake's dropzone: short enough to sit under a textarea
            // without owning the card, and tall enough that the compact prompt still
            // states both the formats and the size cap.
            height={140}
            galleryTitle="Attachments"
            onFilesChange={files => {
              // FileUploadField hands back its own wrapper ({id, file, …}), not native
              // Files — same unwrap as the intake and as useProductMutations.
              setAttachments((files as unknown as { file: File }[]).map(wrapper => wrapper.file))
            }}
          />
        </Stack>

        <Stack direction="row" spacing={3} justifyContent="flex-end">
          <Button variant="primary" disableElevation disabled={!canSend} onClick={send}>
            {/* Three states, not two. "Uploading…" is a separate word from "Sending…"
                because with a 25 MB screen recording attached the upload leg is the long
                one, and a button that says "Sending…" for eight seconds while nothing is
                being sent yet is the button lying. */}
            {isUploading ? 'Uploading…' : isPending ? 'Sending…' : 'Send reply'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  )
}
